/**
 * Pure betting-action engine for the table-based hand builder.
 *
 * The builder lays out the whole table up front (seats, hole cards, board), then
 * the user enters the action one street at a time. This module is the brain that
 * makes that feel like poker rather than a form: given the seats placed and the
 * actions so far, it knows whose turn it is, what the legal actions are, and what
 * it costs to call — including the blinds, which are posted on the felt so the
 * preflop action starts against a standing 1bb bet (you must call 1bb to play).
 *
 * It mirrors the backend's `pot_math` / `action_facts` semantics (action `amount`
 * is the absolute bet LEVEL for the street), generalized from "hero" to every
 * seat. No rendering, no state — pure functions over the BuiltHand model.
 */

import type { BuiltHand, HandActionKind } from "@/shared/models";
import { POSITIONS_CLOCKWISE } from "@/features/chat/lib/seatLayout";

export const SMALL_BLIND = 0.5;
export const BIG_BLIND = 1;

export type StreetKey = "preflop" | "flop" | "turn" | "river";
const STREETS_IN_ORDER: StreetKey[] = ["preflop", "flop", "turn", "river"];

// Preflop acts in clockwise order (UTG first … BB last); postflop starts at the
// SB and runs to the BTN. Filtered to the seats actually in the hand.
const POSTFLOP_ORDER = [
  ...POSITIONS_CLOCKWISE.slice(7), // SB, BB
  ...POSITIONS_CLOCKWISE.slice(0, 7), // UTG … BTN
];

/** Seat labels in the hand: hero + each villain. */
export function placedSeats(hand: BuiltHand): string[] {
  return [hand.heroPosition, ...hand.villains.map((v) => v.position)];
}

/** Action label for a seat ("Hero" for the hero seat, else the seat itself). */
export function actorForSeat(hand: BuiltHand, seat: string): string {
  return seat === hand.heroPosition ? "Hero" : seat;
}

/** Inverse of {@link actorForSeat}: the seat an action label refers to. */
export function seatForActor(hand: BuiltHand, actor: string): string {
  return actor === "Hero" ? hand.heroPosition : actor;
}

/** Chips a seat has posted before any voluntary action (the blinds, preflop). */
export function startingContribution(seat: string, street: StreetKey): number {
  if (street !== "preflop") return 0;
  if (seat === "BB") return BIG_BLIND;
  if (seat === "SB") return SMALL_BLIND;
  return 0;
}

function startingBetLevel(street: StreetKey): number {
  return street === "preflop" ? BIG_BLIND : 0;
}

/** Seats acting on `street`, in turn order, that haven't folded on a PRIOR
 *  street (they're still in the hand entering this street). */
export function actionOrder(hand: BuiltHand, street: StreetKey): string[] {
  const placed = new Set(placedSeats(hand));
  const folded = foldedBefore(hand, street);
  const order = street === "preflop" ? POSITIONS_CLOCKWISE : POSTFLOP_ORDER;
  return order.filter((s) => placed.has(s) && !folded.has(s));
}

/** Seats that folded on any street before `street`. */
export function foldedBefore(hand: BuiltHand, street: StreetKey): Set<string> {
  const idx = STREETS_IN_ORDER.indexOf(street);
  const folded = new Set<string>();
  for (let i = 0; i < idx; i++) {
    const acts = hand.streets[STREETS_IN_ORDER[i]]?.actions ?? [];
    for (const a of acts) {
      if (a.action === "fold") folded.add(seatForActor(hand, a.actor));
    }
  }
  return folded;
}

export interface StreetState {
  /** Chips each still-in seat has committed this street (incl. posted blinds). */
  committed: Record<string, number>;
  /** The current bet level seats must match. */
  level: number;
  /** Seats that folded THIS street. */
  foldedThisStreet: Set<string>;
  /** Still-in seats this street (not folded before or during). */
  remaining: string[];
  /** The seat whose turn it is, or null when the round is closed. */
  nextToAct: string | null;
  /** Whether the betting round is complete (everyone matched + had a turn). */
  closed: boolean;
}

/** Replay a street's entered actions to derive the live betting state. */
export function streetState(hand: BuiltHand, street: StreetKey): StreetState {
  const seatsInOrder = actionOrder(hand, street);
  const committed: Record<string, number> = {};
  for (const s of seatsInOrder) committed[s] = startingContribution(s, street);

  let level = startingBetLevel(street);
  const foldedThisStreet = new Set<string>();
  // Seats that have voluntarily acted since the last bet/raise. Posting a blind
  // is NOT acting — so the BB still gets its option, and the SB still owes the
  // half-blind, both of which fall out of this set being empty for them.
  let actedSinceAggression = new Set<string>();
  let lastActorSeat: string | null = null;

  for (const a of hand.streets[street]?.actions ?? []) {
    const seat = seatForActor(hand, a.actor);
    switch (a.action) {
      case "fold":
        foldedThisStreet.add(seat);
        actedSinceAggression.add(seat);
        break;
      case "check":
        actedSinceAggression.add(seat);
        break;
      case "call":
        committed[seat] = level;
        actedSinceAggression.add(seat);
        break;
      case "bet":
      case "raise":
        if (a.amount != null) {
          level = a.amount;
          committed[seat] = a.amount;
          actedSinceAggression = new Set([seat]); // everyone else owes a turn again
        }
        break;
    }
    lastActorSeat = seat;
  }

  const remaining = seatsInOrder.filter((s) => !foldedThisStreet.has(s));

  // A seat is "owed" a turn if it hasn't acted since the last aggression, or
  // hasn't matched the current level. The round closes when no seat is owed.
  const owed = (s: string) =>
    !actedSinceAggression.has(s) || (committed[s] ?? 0) < level;

  let nextToAct: string | null = null;
  if (remaining.length >= 2) {
    const startIdx = lastActorSeat
      ? seatsInOrder.indexOf(lastActorSeat) + 1
      : 0;
    for (let i = 0; i < seatsInOrder.length; i++) {
      const seat = seatsInOrder[(startIdx + i) % seatsInOrder.length];
      if (!foldedThisStreet.has(seat) && owed(seat)) {
        nextToAct = seat;
        break;
      }
    }
  }

  return {
    committed,
    level,
    foldedThisStreet,
    remaining,
    nextToAct,
    closed: nextToAct === null,
  };
}

/** Chips the seat must add to call the current bet (0 when not facing one). */
export function toCall(state: StreetState, seat: string): number {
  return Math.max(0, state.level - (state.committed[seat] ?? 0));
}

/** The legal actions for the seat whose turn it is, given the bet it faces. */
export function legalActions(state: StreetState, seat: string): HandActionKind[] {
  if (toCall(state, seat) > 0) return ["fold", "call", "raise"];
  // Facing no bet: check or open. (Folding when you can check for free is legal
  // but never correct, so we don't surface it — keeps the UI honest.)
  return ["check", "bet"];
}

/** Convenience: the seat to act + its options, or null when the round is done. */
export function nextDecision(
  hand: BuiltHand,
  street: StreetKey,
): { seat: string; toCall: number; actions: HandActionKind[] } | null {
  const state = streetState(hand, street);
  if (state.nextToAct === null) return null;
  return {
    seat: state.nextToAct,
    toCall: toCall(state, state.nextToAct),
    actions: legalActions(state, state.nextToAct),
  };
}

const _round1 = (v: number) => Math.round(v * 10) / 10;

/** The pot at the moment of the decision on `street` — every seat's committed
 *  chips across all streets up to here, plus the dead blinds for any SB/BB that
 *  isn't a tracked seat (their 1.5bb is still live). Drives the %-of-pot presets,
 *  mirroring the backend's `pot_math` baseline. */
export function potAtDecision(hand: BuiltHand, street: StreetKey): number {
  const upto = STREETS_IN_ORDER.slice(0, STREETS_IN_ORDER.indexOf(street) + 1);
  let pot = 0;
  for (const s of upto) {
    for (const v of Object.values(streetState(hand, s).committed)) pot += v;
  }
  const placed = new Set(placedSeats(hand));
  if (!placed.has("SB")) pot += SMALL_BLIND; // dead small blind
  if (!placed.has("BB")) pot += BIG_BLIND; // dead big blind
  return _round1(pot);
}

export interface SizingPreset {
  label: string;
  /** The absolute bet LEVEL ("to" amount) for the street. */
  amount: number;
}

/** Context-aware bet/raise presets, mirroring the drill SpotCard's
 *  `computePresets`: preflop = multiples of the standing bet (or 2–4× opens),
 *  postflop = %-of-pot when leading / raise multiples when facing a bet. Each
 *  amount is the absolute "to" level, capped at the effective stack. */
export function sizingPresets(hand: BuiltHand, street: StreetKey): SizingPreset[] {
  const state = streetState(hand, street);
  const stack = hand.effectiveStackBb || Infinity;
  const cap = (v: number) => Math.min(_round1(v), stack);
  const pot = potAtDecision(hand, street);

  let raw: SizingPreset[];
  if (street === "preflop") {
    raw =
      state.level > BIG_BLIND
        ? [
            { label: "2.5×", amount: cap(state.level * 2.5) },
            { label: "3×", amount: cap(state.level * 3) },
            { label: "4×", amount: cap(state.level * 4) },
            { label: "All-in", amount: stack },
          ]
        : [
            { label: "2×", amount: cap(2) },
            { label: "2.5×", amount: cap(2.5) },
            { label: "3×", amount: cap(3) },
            { label: "4×", amount: cap(4) },
          ];
  } else if (state.level > 0) {
    // Facing a postflop bet → raise sizings relative to that bet + the pot.
    raw = [
      { label: "2.5×", amount: cap(state.level * 2.5) },
      { label: "3×", amount: cap(state.level * 3) },
      { label: "Pot+", amount: cap(pot) },
      { label: "All-in", amount: stack },
    ];
  } else {
    // Leading postflop → %-of-pot.
    raw = [
      { label: "33%", amount: cap(pot * 0.33) },
      { label: "50%", amount: cap(pot * 0.5) },
      { label: "75%", amount: cap(pot * 0.75) },
      { label: "Pot", amount: cap(pot) },
    ];
  }
  // Drop any preset that isn't a positive, legal raise above the current level.
  return raw.filter((p) => p.amount > state.level);
}
